import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useParams } from "react-router-dom";
import { Button, RenderWhen } from "../../../../components";
import SubmitUrlModal from "../../../../components/SubmitUrlModal";
import Typography from "../../../../components/typography/Typography";
import useModal from "../../../../hooks/useModal";
import { useGetMergedRlcsURLReport } from "../../../samples/components/rlcloudsandbox/hooks/useGetMergedRlcsURLReport";
import NetworkThreatActionsButton from "../common/NetworkThreatActionsButton";
import useUrlAnalysisStatus from "../../hooks/useUrlAnalysisStatus";

import { URL_DYNAMIC_ANALYSIS_STATUS_QUERY_KEY } from "../../hooks/useUrlDynamicAnalysisStatus";
import styles from "./top-header.module.scss";
import TopHeader from "./TopHeader";
import InteractiveAnalysisLink from "../../../samples/components/interactiveanalysis/components/interactive-analysis-link/InteractiveAnalysisLink";

type NetworkThreatTopHeaderProps = {
  finalUrl?: string;
  isError?: boolean;
  isValidationError?: boolean;
};

const NetworkThreatTopHeader: React.FC<NetworkThreatTopHeaderProps> = ({
  finalUrl,
  isError = false,
  isValidationError = false
}) => {
  const queryClient = useQueryClient();
  const { url: _url = "", ip: _ip = "", domain: _domain = "" } = useParams();

  const [showReanalyzeModal, setShowReanalyzeModal] = useModal();

  const { data: mergedReport } = useGetMergedRlcsURLReport(_url);
  const { isAnalyzing, refetch: refetchUrlAnalysisStatus } =
    useUrlAnalysisStatus(_url);

  const { general_info: { sha1 = "" } = {}, history_analysis: history = [] } =
    mergedReport ?? {};

  const { analysis_id: analysisId = "" } = history?.at(0) ?? {};

  const handleOpenReanalyzeModal = () => {
    setShowReanalyzeModal(true);
  };

  const handleCloseReanalyzeModal = () => {
    setShowReanalyzeModal(false);
    queryClient.refetchQueries({
      queryKey: [URL_DYNAMIC_ANALYSIS_STATUS_QUERY_KEY, _url]
    });
    refetchUrlAnalysisStatus();
  };

  return (
    <>
      <div className="network-threat-intel__top-header-container">
        <div className="flex flex-column width-70-pc md:width-60-pc gap-sm">
          <TopHeader
            type={_url ? "url" : _domain ? "domain" : "ip"}
            value={_url || _domain || _ip}
            noMarginBottom
          />
          <RenderWhen condition={!!finalUrl && !isAnalyzing}>
            <div className="network-threat-intel__top-header-subtitle overflow-ellipsis">
              <span className="network-threat-intel__top-header-subtitle-label">
                Final URL
              </span>
              : {finalUrl}
            </div>
          </RenderWhen>
        </div>

        <RenderWhen condition={Boolean(_url)}>
          <div className="flex gap-sm align-center">
            <RenderWhen condition={isAnalyzing}>
              <InteractiveAnalysisLink url={_url} />
            </RenderWhen>
            <Button
              id={styles.nti_report_summary_reanalyze}
              variant="secondary"
              onClick={handleOpenReanalyzeModal}
              size="md"
              disabled={isAnalyzing || isValidationError}
            >
              <Typography tag="span" variant="subtitle2">
                REANALYZE
              </Typography>
            </Button>
            <NetworkThreatActionsButton
              sha1={sha1}
              analysisId={analysisId}
              disabled={isAnalyzing || isError}
            />
          </div>
        </RenderWhen>
      </div>
      <RenderWhen condition={showReanalyzeModal}>
        <SubmitUrlModal
          key={_url}
          url={_url}
          show={showReanalyzeModal}
          handleShow={handleCloseReanalyzeModal}
        />
      </RenderWhen>
    </>
  );
};

export default NetworkThreatTopHeader;
